import { useEffect, useImperativeHandle, useRef, useState, type Ref } from "react";

export interface ComposerHandle {
  /** Drop text into the box and focus it, without sending. */
  prefill: (text: string) => void;
}

export function Composer({
  busy,
  suggestions,
  onSend,
  ref,
}: {
  busy: boolean;
  suggestions: string[];
  onSend: (text: string) => void;
  ref?: Ref<ComposerHandle>;
}) {
  const [value, setValue] = useState("");
  const box = useRef<HTMLTextAreaElement>(null);

  useImperativeHandle(ref, () => ({
    prefill(text: string) {
      setValue(text);
      requestAnimationFrame(() => {
        box.current?.focus();
        box.current?.setSelectionRange(text.length, text.length);
      });
    },
  }));

  // Grow with the text, up to a ceiling.
  useEffect(() => {
    const node = box.current;
    if (!node) return;
    node.style.height = "auto";
    node.style.height = Math.min(node.scrollHeight, 160) + "px";
  }, [value]);

  const submit = () => {
    const text = value.trim();
    if (!text || busy) return;
    setValue("");
    onSend(text);
  };

  return (
    <div className="composer-wrap">
      {suggestions.length > 0 && (
        <div className="suggestions">
          <span className="suggestions-label">Narrow it down</span>
          {suggestions.map((s) => (
            <button className="chip" type="button" key={s} onClick={() => onSend(s)} disabled={busy}>
              {s}
            </button>
          ))}
        </div>
      )}

      <form
        className="composer"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <textarea
          ref={box}
          rows={1}
          value={value}
          disabled={busy}
          placeholder="A pet-friendly 2 bedroom in Leslieville under $3,500…"
          enterKeyHint="send"
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
        />
        <button className="send-btn" type="submit" aria-label="Send" disabled={busy || !value.trim()}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </button>
      </form>

      <p className="disclaimer">
        Building data from the City of Toronto open data portal. Unit availability and rents are sample data,
        not live inventory.
      </p>
    </div>
  );
}
